import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getFragmentData } from '../graphql/generated';
import type { PatientDataInput } from '../graphql/generated/graphql.ts';
import { FormFieldType, type RequestCopyDataFragment } from '../graphql/generated/graphql.ts';
import { stripTypename } from '../utils/helpers';
import { useAppStore } from './store/useAppStore';
import { REQUEST_COPY_QUERY, RequestCopyFragment } from './useRequestCopy.gql';

export const useRequestCopy = (
  type: 'update' | 'new',
  updatePatientData: boolean,
  currentPatientData: PatientDataInput | null,
  onContextUpdated: (data: RequestCopyDataFragment) => void
) => {
  const {
    executeRequestCopy,
    setExecuteRequestCopy,
    requestIdForUpdate,
    requestIdForNew,
    setParameterIdsWithUnknownBilling,
    setPatientData,
    setAdditionalData,
    setDiagnose,
    setLabInfo,
    setComment,
    setAcute,
    setFormData,
    setSampledAt,
    setParamQueue,
    setSelectedProfiles,
    setLocalizations,
    setElgaOptOut,
  } = useAppStore(
    useShallow(state => ({
      executeRequestCopy: state.executeRequestCopy,
      setExecuteRequestCopy: state.setExecuteRequestCopy,
      requestIdForUpdate: state.requestIdForUpdate,
      requestIdForNew: state.requestIdForNew,
      setParameterIdsWithUnknownBilling: state.setParameterIdsWithUnknownBilling,
      setPatientData: state.setPatientData,
      setAdditionalData: state.setAdditionalData,
      setDiagnose: state.setDiagnose,
      setLabInfo: state.setLabInfo,
      setComment: state.setComment,
      setAcute: state.setAcute,
      setFormData: state.setFormData,
      setSampledAt: state.setSampledAt,
      setParamQueue: state.setParamQueue,
      setSelectedProfiles: state.setSelectedProfiles,
      setLocalizations: state.setLocalizations,
      setElgaOptOut: state.setElgaOptOut,
    }))
  );

  const { data } = useQuery(REQUEST_COPY_QUERY, {
    variables: {
      requestId: type === 'update' ? requestIdForUpdate : requestIdForNew,
      currentPatientData: currentPatientData,
    },
    skip: type === 'update' ? !requestIdForUpdate : !requestIdForNew,
  });

  const requestCopy = getFragmentData(RequestCopyFragment, data?.requestCopy);

  useEffect(() => {
    if (requestCopy && executeRequestCopy) {
      setParamQueue(state => ({
        ...state.paramQueue,
        selectedParameters:
          requestCopy?.selectedParameters.map(it => ({
            ...it.requestableParameter,
            billingInfo: it.billingInfo,
          })) ?? [],
      }));

      // the copied request replaces the whole selection — profiles picked before it are gone with it
      setSelectedProfiles([]);

      setParameterIdsWithUnknownBilling(requestCopy.unknownBillingParameters.map(it => it.id));

      setFormData(
        requestCopy.orderForms
          .filter(it => (type === 'new' ? it.restore : true))
          .flatMap(it => it.fields)
          .filter(it => it.values.length)
          .reduce((result, field) => {
            switch (field.type) {
              case FormFieldType.DATE:
                result[field.formFieldId] = dayjs(field.values[0]);
                break;
              case FormFieldType.INPUT:
              case FormFieldType.OPTION_GROUP:
              case FormFieldType.SELECT:
                result[field.formFieldId] = field.values[0];
                break;
              default:
                result[field.formFieldId] = field.values;
            }
            return result;
            // biome-ignore lint/suspicious/noExplicitAny: dynamic reduce accumulator
          }, {} as any)
      );

      if (updatePatientData) {
        const patientData = stripTypename(requestCopy.patientData);
        if (patientData.insuredPerson) {
          patientData.insuredPerson = stripTypename(patientData.insuredPerson);
        }
        setPatientData(patientData);
      }
      setDiagnose(requestCopy.diagnose);
      setLabInfo(requestCopy.labInfo);
      setComment(requestCopy.comment);
      setAcute(requestCopy.acute);
      setAdditionalData(requestCopy.additionalData);
      /**
       * The recorded values are a snapshot taken when the sample was ordered; the Material's
       * configuration may have moved on since. A label the Material no longer has is rejected by
       * RequestService.validateFieldValues on send, and the client-side check never sees it - it
       * iterates the Material's current fields - so the copy would become unsendable with no field
       * to put the error on. Dropping it here loses a value that could not have been submitted
       * anyway.
       */
      const configuredFields = new Map(
        requestCopy.selectedParameters
          .flatMap(it => it.requestableParameter.specimens)
          .map(spec => [spec.id, new Set(spec.localizationFields.map(field => field.label))])
      );

      // unit is server-derived and not part of the store shape
      setLocalizations(
        requestCopy.localizations.map(it => {
          const known = configuredFields.get(it.specimenId);
          return {
            paramId: it.paramId,
            specimenId: it.specimenId,
            location: it.location,
            freeText: it.freeText,
            fieldValues: it.fieldValues
              // no entry at all means the Material is not in this selection, and an orphaned
              // Lokalisation is never validated - leave those untouched
              .filter(v => !known || known.has(v.label))
              .map(v => ({ label: v.label, value: v.value })),
          };
        })
      );
      setElgaOptOut(requestCopy.elgaOptOut);

      if (type === 'update') {
        setSampledAt(requestCopy.sampledAt || null);
      }

      onContextUpdated(requestCopy);
      setExecuteRequestCopy(false);
    }
  });

  return null;
};
